import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Clock, Search, Tag, User } from "lucide-react";
import { blogs as blogData } from "../../../data/blogs";
import "./Blogs.css";

// const categories = ["All", "Company News", "Partnerships", "AI & ML"];

const blogs = blogData.map((b) => ({
  id: b.slug,
  title: b.title,
  excerpt: b.excerpt,
  image: b.image,
  banner: b.banner,
  category: b.category,
  author: b.author,
  date: b.date,
  read: b.read,
  tag: b.tag,
}));

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const Blogs = () => {
  // const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");

  const featured = blogs[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = blogs.slice(1); // exclude featured

    if (!q) return list;

    return list.filter((b) =>
      `${b.title} ${b.excerpt} ${b.author} ${b.category} ${b.tag || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [query]);

  return (
    <div className="bg-page">
      {/* HERO */}
      <section className="bg-hero">
        <div className="bg-hero__bg" />
        <div className="bg-hero__inner">
          <motion.div
            initial="hidden"
            animate="show"
            variants={stagger}
            className="bg-hero__content"
          >
            <motion.span variants={fadeUp} className="bg-hero__chip">
              <Tag size={14} /> SpringUp Insights
            </motion.span>
            <motion.h1 variants={fadeUp} className="bg-hero__title">
              Stories, ideas & playbooks <br />
              from the <span className="bg-hero__accent">SpringUp</span> team
            </motion.h1>
            <motion.p variants={fadeUp} className="bg-hero__sub">
              Deep-dives on AI, cloud, product and engineering — written by builders, for builders.
            </motion.p>
            <motion.div variants={fadeUp} className="bg-hero__search">
              <Search size={18} className="bg-hero__searchIcon" />
              <input
                type="text"
                placeholder="Search articles, topics, authors…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" onClick={() => setQuery("")}>
                  Clear
                </button>
              )}
              {/* <button type="button">Search</button> */}
            </motion.div>
            {/* <motion.div variants={fadeUp} className="bg-hero__stats">
              <div>
                <strong>120+</strong>
                <span>Articles</span>
              </div>
              <div>
                <strong>25k</strong>
                <span>Monthly readers</span>
              </div>
              <div>
                <strong>15</strong>
                <span>Expert authors</span>
              </div>
            </motion.div> */}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="bg-hero__visual"
          >
            <div className="bg-hero__tile bg-hero__tile--a">
              <img src="https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=600&q=80" alt="" />
            </div>
            <div className="bg-hero__tile bg-hero__tile--b">
              <img src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&q=80" alt="" />
            </div>
            <div className="bg-hero__tile bg-hero__tile--c">
              <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&q=80" alt="" />
            </div>
            <div className="bg-hero__badge">
              <span className="bg-hero__badgeDot" /> New post weekly
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-featured">
        <motion.article
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="bg-featured__card"
        >
          <div className="bg-featured__media">
            <img src={featured.image} alt={featured.title} />
            <span className="bg-featured__tag">{featured.tag}</span>
          </div>
          <div className="bg-featured__body">
            <span className="bg-featured__cat">{featured.category}</span>
            <h2>{featured.title}</h2>
            <p>{featured.excerpt}</p>
            <div className="bg-featured__meta">
              <span><User size={14} /> {featured.author}</span>
              <span><Calendar size={14} /> {featured.date}</span>
              <span><Clock size={14} /> {featured.read}</span>
            </div>
            <Link to={`/blogs/${featured.id}`} className="bg-readmore bg-readmore--solid">
              Read full story <ArrowRight size={16} />
            </Link>
          </div>
        </motion.article>
      </section>

      {/* FILTERS */}
      {/* <section className="bg-filters">
        <div className="bg-filters__row">
          {categories.map((c) => (
            <button
              key={c}
              className={`bg-chip ${active === c ? "bg-chip--active" : ""}`}
              onClick={() => setActive(c)}
            >
              {c}
            </button>
          ))}
        </div>
      </section> */}

      {/* GRID */}
      {/* GRID */}
      <section className="bg-grid container">
        <motion.div
          key={query.trim() || "all"}   // ← remount when search clears
          variants={stagger}
          initial="hidden"
          animate="show"                // ← use animate, not whileInView
          className="bg-grid__inner"
        >
          {filtered.map((b) => (
            <motion.article key={b.id} variants={fadeUp} className="bg-card">
              <div className="bg-card__media">
                <img src={b.banner} alt={b.title} />
                <span className="bg-card__cat">{b.category}</span>
              </div>
              <div className="bg-card__body">
                <h3>{b.title}</h3>
                <p>{b.excerpt}</p>
                <div className="bg-card__meta">
                  <span><User size={13} /> {b.author}</span>
                  <span><Clock size={13} /> {b.read}</span>
                </div>
                <Link to={`/blogs/${b.id}`} className="bg-readmore">
                  Read more <ArrowRight size={15} />
                </Link>
              </div>
              <span className="bg-card__corner bg-card__corner--tl" />
              <span className="bg-card__corner bg-card__corner--br" />
            </motion.article>
          ))}

          {filtered.length === 0 && (
            <div className="bg-empty">
              No articles found. Try a different search.
            </div>
          )}
        </motion.div>
      </section>

      {/* NEWSLETTER */}
      {/* <section className="bg-news">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="bg-news__card"
        >
          <div>
            <h3>Get the SpringUp newsletter</h3>
            <p>Fresh insights on AI, cloud and product — straight to your inbox, every other week.</p>
          </div>
          <form className="bg-news__form" onSubmit={(e) => e.preventDefault()}>
            <input type="email" placeholder="you@company.com" required />
            <button type="submit">
              Subscribe <ArrowRight size={16} />
            </button>
          </form>
        </motion.div>
      </section> */}
    </div>
  );
};

export default Blogs;